import React, { useEffect, useState } from "react";
import {
    CheckBoxOutlineBlankOutlined,
    CheckBoxOutlined,
    FileDownloadOutlined,
} from "@mui/icons-material";
import {
    Checkbox,
    FormControlLabel,
    IconButton,
    Tooltip,
    Box,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";

import { useLazyListLocalTransportQuery } from "store/services/localTransportApi";
import {
    useLazyGetDDLBranchListQuery,
    useLazyGetDDLDestinationBranchListQuery,
} from "store/services/branchApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useAuth } from "hooks";

import { exportExcelFile } from "utils/exportExcelFile";
import { localizeDateShort } from "utils/dateFormats";

const INITIAL_FORM = {
    dateRange: [],
    fromBranch: null,
    toBranch: null,
    transportationMode: null,
};

const ExportLocalTransportExcel = ({ isBusy = false }) => {
    const [open, setOpen] = useState(false);
    const [showFromBranchFilter, setShowFromBranchFilter] = useState(false);
    const [showToBranchFilter, setShowToBranchFilter] = useState(false);
    const [showModeFilter, setShowModeFilter] = useState(false);

    const { viewEntity } = useAuth();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({ defaultValues: INITIAL_FORM });

    const watchFromBranch = watch("fromBranch");

    // RTK Queries
    const [fetchLocalTransports, { isLoading: downloading }] =
        useLazyListLocalTransportQuery();

    // Source branches — restricted
    const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
        useLazyGetDDLBranchListQuery();

    // Destination branches — all branches
    const [fetchDestinationBranches, { data: destinationBranches, isLoading: loadingDestinationBranches }] =
        useLazyGetDDLDestinationBranchListQuery();

    const { data: transportModeList = [], isLoading: loadingModes } =
        useListListQuery(
            {
                type: ENTITY_LIST_CONSTANTS.TRANSPORT_MODE,
                status: "Active",
            },
            { skip: !open }
        );

    // Load branches only when dialog opens
    useEffect(() => {
        if (!open) return;
        const account = viewEntity?.entity?.account?._id;
        const entity = viewEntity?.entity?._id;
        if (account && entity) {
            fetchBranches({ account, entity });
            fetchDestinationBranches({ account, entity });
        }
    }, [open, fetchBranches, fetchDestinationBranches, viewEntity]);

    // Filter toBranch options (exclude selected fromBranch)
    const filteredToBranches = React.useMemo(() => {
        if (!destinationBranches || !watchFromBranch) return destinationBranches || [];
        return destinationBranches.filter(branch => branch._id !== watchFromBranch._id);
    }, [destinationBranches, watchFromBranch]);

    const onDownload = async (form) => {
        const { dateRange, fromBranch, toBranch, transportationMode } = form;

        if (!dateRange?.[0] || !dateRange?.[1]) {
            toast.error("Please select a valid date range.");
            return;
        }

        const params = {
            active: "true",
            limit: 10000,
            fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
            toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
            ...(showFromBranchFilter && fromBranch && { branch: fromBranch._id }),
            ...(showToBranchFilter && toBranch && { toBranch: toBranch._id }),
            ...(showModeFilter && transportationMode && { transportationMode: transportationMode._id }),
        };

        try {
            const response = await fetchLocalTransports(params);

            if (response.error) {
                toast.error("Failed to fetch local transport data.");
                console.error("API Error:", response.error);
                return;
            }

            const data = response.data?.data || response.data || [];

            if (!data || data.length === 0) {
                toast.info("No records found for selected filters.");
                return;
            }

            const sheetData = data.map((row, i) => ({
                SN: i + 1,
                "Transport ID": row.ltTag || `LT-${String(row._id).slice(-6)}`,
                "From Branch": row.branch?.name || "N/A",
                "To Branch": row.toBranch?.name || "N/A",
                "No. of Sacks": row.noOfSacks || 0,
                "Transportation Mode": row.transportationMode?.name || "N/A",
                "Distance (KM)": row.distanceKm || 0,
                "Estimated Cost": row.estimatedCost || 0,
                "Actual Cost": row.actualCost || 0,
                Status: row.status || "N/A",
                "Requested By": row.requestBy?.name || "N/A",
                "Approved By": row.approver?.name || "N/A",
                "Created Date": row.createdAt ? localizeDateShort(row.createdAt) : "N/A",
                Remarks: row.remarks || "",
            }));

            exportExcelFile({
                sheetData,
                fileName: `LocalTransport_${moment().format("YYYYMMDD_HHmmss")}`,
            });

            const totalItems = response.data?.totalItems || data.length;

            if (totalItems > data.length) {
                toast.info(`Downloaded ${data.length} of ${totalItems} records.`);
            } else {
                toast.success(`Excel downloaded successfully! ${data.length} records exported.`);
            }
        } catch (err) {
            console.error("Export error:", err);
            toast.error("Failed to download data. Please try again.");
        } finally {
            onClose();
        }
    };

    const onClose = () => {
        setOpen(false);
        setShowFromBranchFilter(false);
        setShowToBranchFilter(false);
        setShowModeFilter(false);
        reset(INITIAL_FORM);
    };

    return (
        <>
            <Tooltip title="Download Excel" arrow>
                <IconButton color="success" onClick={() => setOpen(true)}>
                    <FileDownloadOutlined />
                </IconButton>
            </Tooltip>

            <AppConfirmDialog
                open={open}
                onClose={onClose}
                onConfirm={handleSubmit(onDownload)}
                title="Download Local Transport Excel"
                loading={downloading || isBusy}
                dialogIcon={() => <FileDownloadOutlined color="success" />}
                sx={{ "& .MuiDialog-paper": { maxWidth: 560, width: "100%" } }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
                    {/* Date Range */}
                    <Controller
                        name="dateRange"
                        control={control}
                        rules={{ required: "Date range is required" }}
                        render={({ field, fieldState }) => (
                            <DateRangeToggle
                                {...field}
                                label="Select Date Range"
                                size="small"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />

                    {/* Filter by From Branch — restricted */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showFromBranchFilter}
                                onChange={(e) => {
                                    setShowFromBranchFilter(e.target.checked);
                                    if (!e.target.checked) {
                                        setValue("fromBranch", null);
                                        setValue("toBranch", null);
                                    }
                                }}
                                icon={<CheckBoxOutlineBlankOutlined />}
                                checkedIcon={<CheckBoxOutlined />}
                                size="small"
                            />
                        }
                        label="Filter by From Branch"
                        sx={{ m: 0 }}
                    />

                    {showFromBranchFilter && (
                        <AppForm loading={isBusy}>
                            <AppForm.Content>
                                <Controller
                                    name="fromBranch"
                                    control={control}
                                    rules={{ required: showFromBranchFilter ? "Select a from branch" : false }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            label="From Branch"
                                            required={showFromBranchFilter}
                                            value={field.value || null}
                                            onChange={(_, v) => {
                                                field.onChange(v);
                                                const currentToBranch = watch("toBranch");
                                                if (currentToBranch && v?._id === currentToBranch._id) {
                                                    setValue("toBranch", null);
                                                }
                                            }}
                                            getOptionLabel={(o) => o?.name || ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={branches || []}        // ← restricted
                                            loading={loadingBranches}
                                            loadingText="Loading branches..."
                                            helperText={errors.fromBranch?.message}
                                            error={!!errors.fromBranch}
                                            size="small"
                                        />
                                    )}
                                />
                            </AppForm.Content>
                        </AppForm>
                    )}

                    {/* Filter by To Branch — all branches */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showToBranchFilter}
                                onChange={(e) => {
                                    setShowToBranchFilter(e.target.checked);
                                    if (!e.target.checked) setValue("toBranch", null);
                                }}
                                icon={<CheckBoxOutlineBlankOutlined />}
                                checkedIcon={<CheckBoxOutlined />}
                                size="small"
                            />
                        }
                        label="Filter by To Branch"
                        sx={{ m: 0 }}
                    />

                    {showToBranchFilter && (
                        <AppForm loading={isBusy}>
                            <AppForm.Content>
                                <Controller
                                    name="toBranch"
                                    control={control}
                                    rules={{ required: showToBranchFilter ? "Select a to branch" : false }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            label="To Branch"
                                            required={showToBranchFilter}
                                            value={field.value || null}
                                            onChange={(_, v) => field.onChange(v)}
                                            getOptionLabel={(o) => o?.name || ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={filteredToBranches || []}   // ← all branches, fromBranch excluded
                                            loading={loadingDestinationBranches}
                                            loadingText="Loading branches..."
                                            helperText={
                                                errors.toBranch?.message ||
                                                (showFromBranchFilter && !watchFromBranch
                                                    ? "Please select from branch first"
                                                    : "")
                                            }
                                            error={!!errors.toBranch}
                                            size="small"
                                            disabled={showFromBranchFilter && !watchFromBranch}
                                        />
                                    )}
                                />
                            </AppForm.Content>
                        </AppForm>
                    )}

                    {/* Filter by Transportation Mode */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showModeFilter}
                                onChange={(e) => {
                                    setShowModeFilter(e.target.checked);
                                    if (!e.target.checked) setValue("transportationMode", null);
                                }}
                                icon={<CheckBoxOutlineBlankOutlined />}
                                checkedIcon={<CheckBoxOutlined />}
                                size="small"
                            />
                        }
                        label="Filter by Transportation Mode"
                        sx={{ m: 0 }}
                    />

                    {showModeFilter && (
                        <AppForm loading={isBusy}>
                            <AppForm.Content>
                                <Controller
                                    name="transportationMode"
                                    control={control}
                                    rules={{ required: showModeFilter ? "Select a transportation mode" : false }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            label="Transportation Mode"
                                            required={showModeFilter}
                                            value={field.value || null}
                                            onChange={(_, v) => field.onChange(v)}
                                            options={transportModeList || []}
                                            getOptionLabel={(o) => o?.name || ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            loading={loadingModes}
                                            loadingText="Loading modes..."
                                            helperText={errors.transportationMode?.message}
                                            error={!!errors.transportationMode}
                                            size="small"
                                        />
                                    )}
                                />
                            </AppForm.Content>
                        </AppForm>
                    )}
                </Box>
            </AppConfirmDialog>
        </>
    );
};

export default ExportLocalTransportExcel;